import { ClarionCheckbox } from '@clarion/ClarionCheckbox';
import { ClarionDialog } from '@clarion/ClarionDialog';
import { useHotkeysContext } from 'react-hotkeys-hook';
import { Fragment } from 'react/jsx-runtime';
import { useHotkeysDialog } from './HotkeysDialogContext';
import { usePref } from './prefs';
import { useClarionHotkeys } from './useClarionHotkeys';

export default function HotkeysDialog() {
  const { hotkeys } = useHotkeysContext();
  const { isOpen, setIsOpen } = useHotkeysDialog();
  const [shortcutsEnabled, setShortcutsEnabled] = usePref('keyboard.shortcutsEnabled', true);

  useClarionHotkeys('?', () => setIsOpen((prev: boolean) => !prev), {
    description: 'Show/hide keyboard shortcuts',
    eventListenerOptions: { capture: true },
    useKey: true,
  });

  // Listed for help only; editors handle Ctrl+J / ⌘J in their own key handlers.
  useClarionHotkeys('ctrl+j', () => {}, {
    description: 'Insert new line in caption cue',
  });

  // Filter out hotkeys without description and group by description
  const groupedHotkeys = hotkeys.reduce(
    (acc, hotkey) => {
      const label = hotkey.description;
      if (!label) {
        return acc;
      }
      if (!acc[label]) {
        acc[label] = [];
      }
      // Check if this hotkey combo is already in the group to avoid duplicates
      if (!acc[label].some((h) => h.hotkey === hotkey.hotkey)) {
        (acc[label] as any).push(hotkey);
      }
      return acc;
    },
    {} as Record<string, typeof hotkeys>,
  );

  return (
    <ClarionDialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Keyboard Shortcuts"
      showCloseButton
    >
      {shortcutsEnabled && (
        <div className="text-sm opacity-70 -mt-4 mb-4">
          Press <kbd>?</kbd> to open this dialog.
        </div>
      )}
      <ClarionCheckbox
        isSelected={shortcutsEnabled}
        onChange={setShortcutsEnabled}
        children="Enable keyboard shortcuts"
      />
      <div className="columns-1 md:columns-2 gap-x-8 mt-6">
        {Object.entries(groupedHotkeys).map(([label, hotkeyGroup], index) => (
          <div
            key={index}
            className="flex gap-2 items-baseline justify-between mb-6 break-inside-avoid"
          >
            <div>{label}</div>
            <div className="flex flex-col gap-2 items-end">
              {hotkeyGroup.map((hotkey, hotkeyIndex) => (
                <KeyCombo key={hotkeyIndex} combo={hotkey.hotkey} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </ClarionDialog>
  );
}

export function KeyCombo({ combo, className = '' }: { combo: string; className?: string }) {
  const keys = combo
    .toLowerCase()
    .split('+')
    .map((key) => key.trim());

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {keys.map((key, index) => (
        <Fragment key={index}>
          <kbd className="inline-flex items-center justify-center min-w-7 h-7 px-2 text-sm font-semibold rounded border shadow-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600">
            {getDisplayKey(key)}
          </kbd>
        </Fragment>
      ))}
    </div>
  );
}

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const keyMap: Record<string, string> = {
  meta: isMac() ? '⌘' : 'Win',
  cmd: '⌘',
  ctrl: 'Ctrl',
  shift: 'Shift',
  alt: isMac() ? '⌥' : 'Alt',
  mod: isMac() ? '⌘' : 'Ctrl',
  enter: '↵',
  return: '↵',
  backspace: '⌫',
  delete: '⌦',
  del: '⌦',
  tab: '⇥',
  esc: 'Esc',
  escape: 'Esc',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  space: 'Space',
} as const;

function getDisplayKey(key: string): string {
  return keyMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
}
